import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setLoading(true);
    const result = await forgotPassword(email.trim().toLowerCase());
    setLoading(false);
    if (result.ok) {
      setSent(true);
    } else {
      setError(result.error || "Something went wrong.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] px-6 py-10" data-testid="forgot-password-page">
      <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="text-sm text-[#6B7280] hover:text-[#1F3A5F] inline-flex items-center gap-1 mb-6"
        >
          <ArrowLeft size={16} /> Back to login
        </Link>

        {!sent ? (
          <>
            <h1 className="text-3xl font-bold text-[#1F3A5F] mb-2">Reset your password</h1>
            <p className="text-[#6B7280] mb-8">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">Email</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl bg-white"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-[#E07A7A] bg-[#FEF3F2] border border-[#FECACA] p-3 rounded-xl">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-success">
            <div className="w-16 h-16 bg-[#EAF6F4] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-[#4FB3A5]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F3A5F] mb-2">Check your email</h2>
            <p className="text-[#6B7280] mb-6">
              If an account exists for <span className="font-medium">{email}</span>, we've sent a reset link.
            </p>
            <Link to="/login">
              <Button
                className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold"
                data-testid="forgot-back-to-login-btn"
              >
                Back to login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
